import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import DatePicker from 'react-datepicker';

import '../WebLayout/Header/style.css'; // legacy global styles other screens still rely on
import '../WebLayout/Header/react-datepicker.css';
import '../../Screens/Vacations/home.css';
import { base_url } from '../../Api/apiConfig';
import { logo, subLogo, footerLogo } from '../../Assets/vacations';

// Date -> mm/dd/yyyy (format used by the Search page)
const formatDateParam = (date) => {
    if (!date) return '';
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${m}/${d}/${date.getFullYear()}`;
};

const scrollToSection = (id, after) => (e) => {
    e.preventDefault();
    if (after) after();
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
};

// variant "home": nav links scroll to sections of /home. variant "inner": nav links go to pages.
export const VacationsHeader = ({ variant = 'inner' }) => {
    const navigate = useNavigate();
    const isLoggedIn = localStorage.getItem('login') !== null;
    const [menuOpen, setMenuOpen] = useState(false);
    const home = variant === 'home';

    const handleLogout = () => {
        localStorage.clear();
        navigate('/login');
    };

    const navItem = (label, id, to) => (
        <li>
            {home
                ? <a href={`#${id}`} onClick={scrollToSection(id, () => setMenuOpen(false))}>{label}</a>
                : <Link to={to} onClick={() => setMenuOpen(false)}>{label}</Link>}
        </li>
    );

    return (
        <div className="vx-home vx-chrome">
            <header className="site-header">
                <div className="container header-inner">
                    <div className="logo-cluster">
                        <a className="logo" href="https://kayifi.com"><img src={logo} alt="Kayifi" /></a>
                        <Link className="subdomain-btn" to="/home" aria-current="page">
                            <span className="roof" aria-hidden="true"><svg viewBox="0 0 100 10" preserveAspectRatio="none"><polyline points="0,10 50,0 100,10" fill="none" stroke="#F8922D" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" /></svg></span>
                            Vacations
                        </Link>
                    </div>
                    <div className="nav-cluster">
                        <Link className="sub-logo" to="/"><img src={subLogo} alt="Kayifi Vacations" /></Link>
                        <nav className="header-nav" aria-label="Primary">
                            <ul className={`nav-main${menuOpen ? ' open' : ''}`} id="navMain">
                                <li><Link to="/listings" onClick={() => setMenuOpen(false)}>Stays</Link></li>
                                {navItem('Destinations', 'destinations', '/home')}
                                {navItem('Packages', 'packages', '/home')}
                                {navItem('Reviews', 'reviews', '/home')}
                            </ul>
                        </nav>
                    </div>
                    <div className="header-actions">
                        <Link className="btn btn-ghost" to="/sign-up">List Your Rental</Link>
                        {isLoggedIn ? (
                            <>
                                <Link className="btn btn-primary" to="/dashboard">Dashboard</Link>
                                <button className="btn btn-ghost" type="button" onClick={handleLogout}>Log Out</button>
                            </>
                        ) : (
                            <Link className="btn btn-primary" to="/login">Sign In</Link>
                        )}
                        <button className="menu-toggle" aria-label="Menu" type="button" onClick={() => setMenuOpen((o) => !o)}>
                            <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 6h16M4 12h16M4 18h16" /></svg>
                        </button>
                    </div>
                </div>
            </header>
        </div>
    );
};

export const SearchPill = () => {
    const navigate = useNavigate();
    const [destination, setDestination] = useState('');
    const [countries, setCountries] = useState([]);
    const [countriesLoading, setCountriesLoading] = useState(true);
    const [showDestinations, setShowDestinations] = useState(false);
    const [checkInDate, setCheckInDate] = useState(null);
    const [checkOutDate, setCheckOutDate] = useState(null);
    const [adultCount, setAdultCount] = useState(0);
    const [petsAllowed, setPetsAllowed] = useState(null); // true / false / null
    const [showGuests, setShowGuests] = useState(false);
    const destinationRef = useRef(null);
    const guestsRef = useRef(null);
    const checkInRef = useRef(null);
    const checkOutRef = useRef(null);

    useEffect(() => {
        fetch(`${base_url}/countries`, { headers: { Accept: 'application/json' } })
            .then((r) => r.json())
            .then((res) => {
                const raw = res?.data?.data ?? res?.data ?? res ?? [];
                setCountries(Array.isArray(raw)
                    ? raw.map((c) => ({ id: c.id, name: (c.name ?? c.country_name ?? c.title ?? '').trim() })).filter((c) => c.name)
                    : []);
            })
            .catch(() => setCountries([]))
            .finally(() => setCountriesLoading(false));
    }, []);

    useEffect(() => {
        const onClickOutside = (e) => {
            if (destinationRef.current && !destinationRef.current.contains(e.target)) setShowDestinations(false);
            if (guestsRef.current && !guestsRef.current.contains(e.target)) setShowGuests(false);
        };
        document.addEventListener('mousedown', onClickOutside);
        return () => document.removeEventListener('mousedown', onClickOutside);
    }, []);

    const filteredCountries = destination.trim()
        ? countries.filter((c) => c.name.toLowerCase().includes(destination.trim().toLowerCase()))
        : countries;

    const handleCheckInChange = (date) => {
        setCheckInDate(date);
        if (checkOutDate && date && checkOutDate < date) setCheckOutDate(null);
        setTimeout(() => checkInRef.current?.setOpen(false, true), 100);
    };

    const handleCheckOutChange = (date) => {
        setCheckOutDate(date);
        setTimeout(() => checkOutRef.current?.setOpen(false, true), 100);
    };

    const handleSearch = () => {
        const params = new URLSearchParams();
        if (destination.trim()) params.set('location', destination.trim());
        if (checkInDate) params.set('check_in', formatDateParam(checkInDate));
        if (checkOutDate) params.set('check_out', formatDateParam(checkOutDate));
        if (adultCount > 0) params.set('guests', String(adultCount));
        if (petsAllowed !== null) params.set('pets', petsAllowed ? '1' : '0');
        navigate(`/search${params.toString() ? `?${params.toString()}` : ''}`);
    };

    return (
        <div className="vx-home vx-chrome">
            <div className="vac-search-sticky" aria-label="Search stays">
                <div className="container">
                    <div className="search-pill" role="search">
                        <div className="field" ref={destinationRef}>
                            <label htmlFor="where">Where</label>
                            <input id="where" type="text" placeholder="Search Destinations" autoComplete="off" value={destination}
                                onChange={(e) => { setDestination(e.target.value); setShowDestinations(true); }}
                                onFocus={() => setShowDestinations(true)}
                                onKeyDown={(e) => { if (e.key === 'Enter') { setShowDestinations(false); handleSearch(); } }} />
                            {showDestinations && (
                                <ul className="dd-list">
                                    {countriesLoading ? (
                                        <li className="dd-note">Loading destinations...</li>
                                    ) : filteredCountries.length ? (
                                        filteredCountries.map((c) => (
                                            <li key={c.id} onMouseDown={(e) => e.preventDefault()} onClick={() => { setDestination(c.name); setShowDestinations(false); }}>{c.name}</li>
                                        ))
                                    ) : (
                                        <li className="dd-note">No destination found</li>
                                    )}
                                </ul>
                            )}
                        </div>
                        <span className="divider" aria-hidden="true" />
                        <div className="field">
                            <label>Check-In</label>
                            <DatePicker
                                ref={checkInRef}
                                selected={checkInDate}
                                onChange={handleCheckInChange}
                                selectsStart
                                startDate={checkInDate}
                                endDate={checkOutDate}
                                minDate={new Date()}
                                placeholderText="Select Check-In"
                                dateFormat="MM/dd/yyyy"
                                shouldCloseOnSelect
                                preventOpenOnFocus
                                onInputClick={() => { checkOutRef.current?.setOpen(false); checkInRef.current?.setOpen(true); }}
                                onClickOutside={() => checkInRef.current?.setOpen(false)}
                            />
                        </div>
                        <span className="divider" aria-hidden="true" />
                        <div className="field">
                            <label>Check-Out</label>
                            <DatePicker
                                ref={checkOutRef}
                                selected={checkOutDate}
                                onChange={handleCheckOutChange}
                                selectsEnd
                                startDate={checkInDate}
                                endDate={checkOutDate}
                                minDate={checkInDate || new Date()}
                                placeholderText="Select Check-Out"
                                dateFormat="MM/dd/yyyy"
                                disabled={!checkInDate}
                                shouldCloseOnSelect
                                preventOpenOnFocus
                                onInputClick={() => { checkInRef.current?.setOpen(false); checkOutRef.current?.setOpen(true); }}
                                onClickOutside={() => checkOutRef.current?.setOpen(false)}
                            />
                        </div>
                        <span className="divider" aria-hidden="true" />
                        <div className="field" ref={guestsRef}>
                            <label>Guests</label>
                            <button type="button" className="guest-trigger" onClick={() => setShowGuests((o) => !o)}>
                                {adultCount > 0 ? `${adultCount} Guest${adultCount > 1 ? 's' : ''}` : 'Add Guests'}
                            </button>
                            {showGuests && (
                                <div className="guest-pop">
                                    <div className="guest-row">
                                        <div><strong>Adults</strong><span>Ages 13 or above</span></div>
                                        <div className="guest-counter">
                                            <button type="button" aria-label="Fewer guests" onClick={() => setAdultCount((n) => Math.max(n - 1, 0))}>−</button>
                                            <b>{adultCount}</b>
                                            <button type="button" aria-label="More guests" onClick={() => setAdultCount((n) => n + 1)}>+</button>
                                        </div>
                                    </div>
                                    <div className="guest-row">
                                        <div><strong>Pets</strong></div>
                                        <div className="guest-pets">
                                            <label><input type="radio" name="vx-pets" checked={petsAllowed === true} onChange={() => setPetsAllowed(true)} /> Yes</label>
                                            <label><input type="radio" name="vx-pets" checked={petsAllowed === false} onChange={() => setPetsAllowed(false)} /> No</label>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                        <button className="btn btn-primary" type="button" onClick={handleSearch}>Search</button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export const VacationsFooter = ({ variant = 'inner' }) => {
    const home = variant === 'home';
    const stayLink = (label, id) => (
        <li>
            {home
                ? <a href={`#${id}`} onClick={scrollToSection(id)}>{label}</a>
                : <Link to="/home">{label}</Link>}
        </li>
    );

    return (
        <div className="vx-home vx-chrome">
            <footer className="site-footer">
                <div className="container">
                    <div className="footer-grid">
                        <div className="footer-brand">
                            <img src={footerLogo} alt="Kayifi Vacations" />
                            <p>vacations.kayifi.com — part of the Kayifi ecosystem. Your Tropical Connection.</p>
                        </div>
                        <div className="footer-col">
                            <h4>Stay</h4>
                            <ul>
                                <li><Link to="/listings">All Stays</Link></li>
                                {stayLink('Destinations', 'destinations')}
                                {stayLink('Reviews', 'reviews')}
                            </ul>
                        </div>
                        <div className="footer-col">
                            <h4>Kayifi Hub</h4>
                            <ul>
                                <li><a href="https://kayifi.com">Kayifi Home</a></li>
                                <li><a href="https://kayifi.com/en/properties">Properties</a></li>
                                <li><a href="https://services.kayifi.com/en">Services</a></li>
                                <li><a href="https://adventures.kayifi.com/en">Adventures</a></li>
                                <li><Link to="/home">Vacations</Link></li>
                            </ul>
                        </div>
                        <div className="footer-col">
                            <h4>Hosts</h4>
                            <ul>
                                <li><Link to="/sign-up">List Your Rental</Link></li>
                                <li><Link to="/privacy">Privacy</Link></li>
                                <li><Link to="/terms">Terms</Link></li>
                            </ul>
                        </div>
                    </div>
                    <div className="footer-bottom">
                        <p>© {new Date().getFullYear()} Kayifi. All rights reserved.</p>
                    </div>
                </div>
            </footer>
        </div>
    );
};
